import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const FAQS = [
  {
    q: "How long does it take to build my website?",
    a: "Most straightforward small-business websites can typically be prepared within approximately 7–10 business days after we receive all required business information, branding, photos, and content. More complex projects may require additional time.",
  },
  {
    q: "Do I need to already own a domain?",
    a: "No. If you already own one, we'll help you connect it. If you don't, we'll help you get one set up as part of the process.",
  },
  {
    q: "Can I request changes to my website after it launches?",
    a: "Yes. Ongoing maintenance and reasonable small content updates are included in the monthly service so you can keep information like hours, services, and contact details current.",
  },
  {
    q: "Is there a long-term contract?",
    a: "No. The monthly service is month-to-month and you can cancel anytime.",
  },
  {
    q: "What happens if I cancel?",
    a: "Your website is hosted and maintained as part of the monthly service. If you cancel, hosting and continued service stop. We'll help make the transition clear before you decide.",
  },
  {
    q: "Do you write the content for my website?",
    a: "We help set up your initial business content based on what you provide (services, about, service area, etc.). We're not a content marketing agency, but we make sure the information reads clearly and professionally.",
  },
  {
    q: "What if I need something more advanced later?",
    a: "The founding customer website is designed to cover what most small businesses need to look professional online. If your needs grow, we can discuss additional work separately.",
  },
  {
    q: "Do you guarantee more customers or traffic?",
    a: "No. We build a professional, mobile-friendly website that helps your business look credible and makes it easier for customers to contact you. We do not guarantee traffic, leads, or search rankings.",
  },
  {
    q: "What information do you need from me to get started?",
    a: "The lead form asks for the basics: business name, contact info, business type, service area, and a short description of what you need. From there we'll follow up to gather any additional details.",
  },
];

export const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="faq" className="bg-white" data-testid="section-faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
        <SectionHeading
          eyebrow="FAQ"
          title="Frequently Asked Questions"
          subtitle="Answers to the questions we hear most often."
          testId="heading-faq"
        />
        <ul className="divide-y divide-goc-border border-y border-goc-border">
          {FAQS.map((item, idx) => {
            const isOpen = openIdx === idx;
            const panelId = `faq-panel-${idx}`;
            const btnId = `faq-btn-${idx}`;
            return (
              <li key={item.q}>
                <h3>
                  <button
                    id={btnId}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                    className="w-full flex items-start justify-between gap-6 py-5 md:py-6 text-left text-goc-text hover:text-goc-primary transition-colors"
                    data-testid={`faq-question-${idx}`}
                  >
                    <span className="text-base md:text-lg font-semibold">
                      {item.q}
                    </span>
                    <span className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-goc-border text-goc-primary">
                      {isOpen ? (
                        <Minus className="h-4 w-4" strokeWidth={2.5} />
                      ) : (
                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                      )}
                    </span>
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={btnId}
                  hidden={!isOpen}
                  className="pb-5 md:pb-6 pr-10"
                  data-testid={`faq-answer-${idx}`}
                >
                  <p className="text-sm md:text-base text-goc-muted leading-relaxed">
                    {item.a}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default FAQ;
